import { useEffect, useState } from "react";
import { X, Eye, Send } from "lucide-react";
import { toast } from "sonner";
import { useVault, REACTIONS } from "@/context/VaultContext";
import { nameOf } from "@/lib/selectors";

const ITEM_MS = 5000;
const ago = (ts) => { const m = Math.floor((Date.now() - ts) / 60000); return m < 1 ? "now" : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h`; };

function StoryImage({ id, mime }) {
  const { getFileBytes } = useVault();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    getFileBytes(`s_${id}`).then((b) => { if (b) { u = URL.createObjectURL(new Blob([b], { type: mime })); setUrl(u); } });
    return () => u && URL.revokeObjectURL(u);
  }, [id, mime, getFileBytes]);
  return url ? <img src={url} alt="" className="max-h-full max-w-full object-contain" /> : null;
}

export function StoryViewer({ author, stories, contacts, onClose }) {
  const { profile, viewStory, replyToStory, reactToStory } = useVault();
  const [reply, setReply] = useState("");
  const [paused, setPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState(0);
  const [idx, setIdx] = useState(0);
  const [start, setStart] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const st = stories[idx];
  const mine = author === profile.code;

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 80); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (paused) setPausedAt(Date.now());
    else if (pausedAt) { setStart((s) => s + (Date.now() - pausedAt)); setPausedAt(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);
  useEffect(() => { if (st) viewStory(st.story_id); setStart(Date.now()); }, [st?.story_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (d) => { const n = idx + d; if (n < 0) return; if (n >= stories.length) return onClose(); setIdx(n); };
  useEffect(() => { if (st && !paused && now - start >= ITEM_MS) go(1); }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!st) return null;
  const c = st.content;
  const elapsed = paused ? pausedAt - start : now - start;
  const react = async (emoji) => {
    await reactToStory(author, st, emoji);
    toast.success(`Reacted ${emoji} · disappears after viewing`);
  };
  const sendReply = async () => {
    const t = reply.trim();
    if (!t) return;
    setReply("");
    await replyToStory(author, st, t);
    toast.success("Private reply sent · burns after reading");
    setPaused(false);
  };
  const viewers = JSON.parse(st.viewed_by || "[]");

  return (
    <div className="fixed inset-0 z-[60] bg-void flex flex-col select-none" data-testid="story-viewer">
      <div className="px-3 pt-[max(0.75rem,env(safe-area-inset-top))] space-y-3">
        <div className="flex gap-1">
          {stories.map((s, i) => (
            <div key={s.story_id} className="h-[2px] flex-1 bg-ash/15 rounded overflow-hidden">
              <div className="h-full bg-stellar" style={{ width: i < idx ? "100%" : i > idx ? "0%" : `${Math.min(100, (elapsed / ITEM_MS) * 100)}%` }} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <p className="font-branding font-semibold uppercase tracking-wider text-sm" data-testid="story-author">{mine ? "Your story" : nameOf(contacts[author])} <span className="text-ash/40 normal-case font-body font-normal ml-2">{ago(st.timestamp)}</span></p>
          <button onClick={onClose} data-testid="story-close-btn" className="p-2 text-ash/70 hover:text-stellar"><X size={22} strokeWidth={1.5} /></button>
        </div>
      </div>
      <div className="relative flex-1 flex items-center justify-center overflow-hidden" data-testid={`story-item-${st.story_id}`}>
        {c.image ? <StoryImage id={st.story_id} mime={c.image.mime} /> : (
          <div className="w-full h-full flex items-center justify-center px-10" style={{ background: c.bg }}>
            <p className="font-branding text-2xl sm:text-4xl font-semibold text-center leading-snug" data-testid="story-text">{c.text}</p>
          </div>
        )}
        <button className="absolute inset-y-0 left-0 w-1/3" onClick={() => go(-1)} data-testid="story-prev-btn" aria-label="Previous" />
        <button className="absolute inset-y-0 right-0 w-2/3" onClick={() => go(1)} data-testid="story-next-btn" aria-label="Next" />
      </div>
      {!mine && (
        <div className="flex justify-center gap-2 px-3 pt-3" data-testid="story-reactions">
          {REACTIONS.map((e, i) => (
            <button key={e} onClick={() => react(e)} data-testid={`story-react-${i}`} className="w-11 h-11 rounded-full bg-nebula border border-rim text-xl hover:border-stellar/50 hover:scale-110 transition-transform">{e}</button>
          ))}
        </div>
      )}
      {!mine && (
        <div className="px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-glass flex items-center gap-2">
          <input data-testid="story-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onFocus={() => setPaused(true)} onBlur={() => !reply && setPaused(false)}
            onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply privately · burns on read"
            className="flex-1 bg-nebula border border-rim rounded-secure px-3 py-2.5 text-sm placeholder-ash/35 focus:outline-none focus:border-stellar/50" />
          <button onClick={sendReply} disabled={!reply.trim()} data-testid="story-reply-send-btn" className="p-2.5 bg-stellar text-void rounded-secure disabled:opacity-40"><Send size={18} strokeWidth={1.5} /></button>
        </div>
      )}
      {mine && (
        <div className="px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-glass text-sm text-ash/60 flex items-center gap-2" data-testid="story-viewers">
          <Eye size={16} strokeWidth={1.5} className="text-stellar" /> Seen by {viewers.length}{viewers.length ? ` · ${viewers.map((v) => nameOf(contacts[v])).join(", ")}` : ""}
        </div>
      )}
    </div>
  );
}
